import { Draft } from '@reduxjs/toolkit'
import { v4 as uuidv4, validate as uuidValidate } from 'uuid'
import { map, set } from 'lodash'

import { LabelingNodeItem, LabelingObject, OBJECT_TOOL_TYPE } from '@/api/LabelingNode/types'
import { LabelingObjectData, LabelingState, ThunkConditionAPI } from '@/stores/slices/Labeling/types'
import { ANNOTATION_TYPE, AnnotationType } from '@/pages/Labeling/types'
import { calcOriginalPointsForDB, calcRotatedPointsForCanvas } from '@/pages/Labeling/utils/canvasUtils'
import { isBox, isBrush, isEllipse, isPolygon } from '@/pages/Labeling/canvas/types'

export const convertToScaledCoordinates = (annotations: LabelingObjectData[], imgWidth: number, imgHeight: number): LabelingObjectData[] => map(annotations, (obj) => {
  if (isBox(obj)) {
    return {
      ...obj,
      x: obj.x / imgWidth,
      y: obj.y / imgHeight,
      width: obj.width / imgWidth,
      height: obj.height / imgHeight,
    }
  }
  if (isEllipse(obj)) {
    return {
      ...obj,
      cx: obj.cx / imgWidth,
      cy: obj.cy / imgHeight,
      rx: obj.rx / imgWidth,
      ry: obj.ry / imgHeight,
    }
  }
  if (isPolygon(obj) || isBrush(obj)) {
    return {
      ...obj,
      points: map(obj.points, (p) => ({ x: p.x / imgWidth, y: p.y / imgHeight })),
    }
  }

  return obj
})

export const convertToNormalCoordinates = (annotations: LabelingObjectData[], imgWidth: number, imgHeight: number): LabelingObjectData[] => map(annotations, (obj) => {
  if (isBox(obj)) {
    return {
      ...obj,
      x: obj.x * imgWidth,
      y: obj.y * imgHeight,
      width: obj.width * imgWidth,
      height: obj.height * imgHeight,
    }
  }
  if (isEllipse(obj)) {
    return {
      ...obj,
      cx: obj.cx * imgWidth,
      cy: obj.cy * imgHeight,
      rx: obj.rx * imgWidth,
      ry: obj.ry * imgHeight,
    }
  }
  if (isPolygon(obj) || isBrush(obj)) {
    return {
      ...obj,
      points: map(obj.points, (p) => ({ x: p.x * imgWidth, y: p.y * imgHeight })),
    }
  }

  return obj
})

export const serializeObject = ({
  o,
  itemId,
  tagOrder,
  includeText,
}: {
  o: LabelingObjectData
  itemId: string
  tagOrder?: number
  includeText?: boolean
}) => {
  const common = {
    labelingItemId: itemId,
    labelingObjectId: o.id || '',
    labelingClassId: o.label || '',
    zOrder: o.zOrder || 0,
  }
  if (o.id && uuidValidate(o.id)) {
    set(common, 'labelingObjectId', undefined)
  }
  const meta = {
    createdAt: o.createdAt,
    modifiedAt: o.modifiedAt,
    tagOrder,
  }

  switch (o.t) {
    case ANNOTATION_TYPE.BOX: {
      /**
       * konva에서 사각형의 좌표 `x`, `y`는 `rotation`이 적용된 top-left 점이지만
       * backend에서의 `x`, `y`는 `rotation`이 0일 때의 top-left 점으로 다르기 때문에
       * `rotation`이 0일 때의 top-left 점으로 변환해 API 호출.
       */
      const {
        xtl, ytl, xbr, ybr,
      } = calcOriginalPointsForDB(o.x, o.y, o.width, o.height, o.rotation)
      return {
        ...common,
        // type: 2,
        type: OBJECT_TOOL_TYPE.RECTANGLE as keyof typeof OBJECT_TOOL_TYPE,
        isFixedRatio: o.isFixedRatio,
        // isPathEditable: o.isPathEditable || false,
        region: {
          xtl,
          ytl,
          xbr,
          ybr,
          rotation: o.rotation,
        },
        metadata: { ...meta },
        text: includeText ? o.text : undefined,
      }
    }
    case ANNOTATION_TYPE.ELLIPSE: {
      return {
        ...common,
        type: OBJECT_TOOL_TYPE.ELLIPSE as keyof typeof OBJECT_TOOL_TYPE,
        isFixedRatio: o.isFixedRatio,
        // isPathEditable: o.isPathEditable,
        region: {
          cx: o.cx,
          cy: o.cy,
          rx: o.rx,
          ry: o.ry,
          rotation: o.rotation,
        },
        metadata: { ...meta },
      }
    }
    case ANNOTATION_TYPE.POLYGON:
      return {
        ...common,
        type: OBJECT_TOOL_TYPE.POLYGON as keyof typeof OBJECT_TOOL_TYPE,
        region: { points: o.points.map((p: { x: any; y: any; }) => `${p.x},${p.y}`).join(';') },
        // isFixedRatio: o.isFixedRatio,
        isPathEditable: o.isPathEditable,
        metadata: { ...meta },
      }
    case ANNOTATION_TYPE.TAG:
      return {
        ...common,
        type: OBJECT_TOOL_TYPE.TAG,
      }
    case ANNOTATION_TYPE.VIDEO_SEGMENT:
      return {
        ...common,
        type: OBJECT_TOOL_TYPE.VIDEO_SEGMENT,
        startMs: o.startMs,
        endMs: o.endMs,
        videoFileId: o.videoFileId,
        startFrameNumber: o.startFrameNumber,
        endFrameNumber: o.endFrameNumber,
        memoContent: o.memoContent,
        metadata: { ...meta },
      }
    default:
      console.error('cannot reach here - invalid object annotation type')
      return {
        labelingObjectId: '',
        labelingClassId: '',
        labelingNodeId: '',
        labelingItemId: '',
        isFixedRatio: false,
        isPathEditable: false,
        metadata: undefined,
        region: undefined,
        type: OBJECT_TOOL_TYPE.RECTANGLE as keyof typeof OBJECT_TOOL_TYPE,
        zOrder: 0,
      }
  }
}

export const transformAnnotObjects = (data: LabelingObject[]): LabelingObjectData[] => (data ?? []).map((o) => {
  const common = {
    id: o._id || uuidv4(),
    label: o?.labelingClass?._id || '', // TODO: class id만 필요한게 맞나?
    createdAt: o.metadata?.createdAt || new Date(),
    modifiedAt: o.metadata?.modifiedAt || new Date(),
    name: o.metadata?.tagOrder,
    zOrder: o.zOrder || 0,
    toolType: o?.toolType || undefined,
  }

  switch (o.type) {
    case OBJECT_TOOL_TYPE.RECTANGLE: {
      const t: AnnotationType = ANNOTATION_TYPE.BOX
      // konva 좌표계에 맞는 형식으로 변환해줌.
      // 회전하지 않은 tl(xtl, ytl), br(xbr, ybr)점을 사용하여
      // 회전한 점(x, y)과 사각형의 너비(width)와 높이(height)을 반환함.
      const {
        x: xtl,
        y: ytl,
        width: w,
        height: h,
      } = calcRotatedPointsForCanvas(o.region?.xtl, o.region?.ytl, o.region?.xbr, o.region?.ybr, o.region?.rotation)
      return {
        ...common,
        t,
        x: xtl,
        y: ytl,
        width: w,
        height: h,
        rotation: o.region?.rotation,
        isFixedRatio: o.isFixedRatio || false,
        confidenceScore: o.confidenceScore,
        text: o.text,
      }
    }
    case OBJECT_TOOL_TYPE.POLYGON: {
      const t: AnnotationType = ANNOTATION_TYPE.POLYGON
      return {
        ...common,
        t,
        points: o.region?.points?.split(';').map((p: string) => {
          const c = p.split(',')
          return {
            x: parseFloat(c[0]),
            y: parseFloat(c[1]),
          }
        }),
        isPathEditable: o.isPathEditable || false,
        confidenceScore: o.confidenceScore,
        text: o.text,
      }
    }
    case OBJECT_TOOL_TYPE.ELLIPSE: {
      const t: AnnotationType = ANNOTATION_TYPE.ELLIPSE
      return {
        ...common,
        t,
        cx: o.region?.cx,
        cy: o.region?.cy,
        rx: o.region?.rx,
        ry: o.region?.ry,
        isFixedRatio: o.region?.isFixedRatio || false,
        rotation: o.region?.rotation,
        confidenceScore: o.confidenceScore,
      }
    }
    case OBJECT_TOOL_TYPE.TAG: {
      const t: AnnotationType = ANNOTATION_TYPE.TAG
      return {
        ...common,
        ...o,
        t,
      }
    }
    case OBJECT_TOOL_TYPE.VIDEO_SEGMENT: {
      const t: AnnotationType = ANNOTATION_TYPE.VIDEO_SEGMENT
      return {
        ...common,
        t,
        startMs: o.startMs ?? 0,
        endMs: o.endMs ?? 0,
        videoFileId: o.videoFileId ?? null,
        startFrameNumber: o.startFrameNumber ?? 0,
        endFrameNumber: o.endFrameNumber ?? 0,
        memoContent: o.memoContent ?? null,
        color: o.labelingClass?.color ?? undefined,
      }
    }
    default: {
      const { region = {}, ...others } = o
      return {
        ...others,
        id: o._id || uuidv4(),
        ...region,
      }
    }
  }
}) ?? []

export function getSettledFiles(state: Draft<LabelingState>): LabelingNodeItem[]
export function getSettledFiles(state: Draft<LabelingState>, index: number): LabelingNodeItem
export function getSettledFiles({ files }: Draft<LabelingState>, index?: number) {
  const isInvalidIndex = index !== undefined && files[index] === null
  if ((files.length < 1 && process.env.NODE_ENV === 'development') || isInvalidIndex) {
    throw new Error(
      'Access to uninitialized annotation file is not allowed. '
      + `Annotation file of index ${index} is null or undefined. `
      + 'Check if the annotation file is properly loaded before access.'
    )
  }
  if (index !== undefined) {
    return files[index]
  }
  return files
}

export const getLabelingDefaultCondition = <A, E>(args: A, thunkAPI: ThunkConditionAPI<E>) => {
  if (thunkAPI.getState().labeling.isInProgress) {
    console.error('Another request is in progress')
    return false
  }
  return true
}
